var Type = require('./Type');
var Alignment = require('../../style/Alignment');

/**
 * The Text class represents textual information without any
 * particular formatting. It provides font and sizing details that
 * can be used by the Styles class.
 *
 * @param {Object} [oConfig] - Configuration object for this Text type
 * @param {Object} [oConfig.font] - Font configuration object
 * @param {number} [oConfig.font.size] - Font size
 * @param {string} [oConfig.font.name] - Font name i.E. Arial, Verdana, etc.
 * @param {Object} [oConfig.fill] - Cell fill configuration object
 * @param {string} [oConfig.fill.color] - Cell fill background color
 * @param {string} [oConfig.fill.type] - Cell fill type
 * @param {string} [oConfig.template] - Text template for this Type
 * @param {string} [oConfig.hierarchyLevel] - Property name that is used for hierarchy level indentation
 * @param {boolean} [oConfig.wrap] - Indicates the wrapping behavior of the text cell
 * @param {string} [oConfig.inputFormat] - Regular expresion that will be used to identify specific parts of the single property value and further process it with a template
 * @constructor
 */
function Text(oConfig) {
	Type.call(this, oConfig);

	this.font = oConfig ? oConfig.font : null;
	this.fill = oConfig ? oConfig.fill : null;
	this.template = oConfig ? oConfig.template : null;
	this.hierarchyLevel = oConfig ? oConfig.hierarchyLevel : null;
	this.wrap = oConfig ? oConfig.wrap : false;
	this.inputFormat = oConfig ? oConfig.inputFormat : null;

	this._mStyleIds = {};
}

Text.prototype = Object.create(Type.prototype);
Text.prototype.constructor = Text;

Text.DEFAULT = {
	font: {
		size: 11,
		name: 'Arial'
	}
};

Text.formulaRegex = /^[\s]*[=+\-@]/;

/**
 * Ensures that all line breaks use CR + LF.
 *
 * @param {string} sValue - Text that may contain line breaks
 * @return {string} - Initial text with replaced line breaks
 */
Text.convertLineBreaks = function(sValue) {
	var compliantLineBreak = String.fromCharCode(13, 10);

	return sValue.replace(/\r/g, '').replace(/\n/g, compliantLineBreak);
};

/**
 * This method creates the XLSX specific style configuration depending on the cell content
 *
 * @param {Object} oData - Line item that will be used to evaluate the cell content
 * @returns {Object} - Style configuration used by the Styles class
 * @protected
 */
Text.prototype._createConfiguration = function(oData) {
	var config;

	config = Object.getPrototypeOf(Text.prototype)._createConfiguration.call(this);
	config.fill = this.fill;
	config.font = this.font;

	/* Overwrites existing alignment for a proper indent */
	if (this.hierarchyLevel) {
		config.align = {
			horizontal: Alignment.horizontal.LEFT,
			indent: 2 * oData[this.hierarchyLevel]
		};
	}

	/* Wrapping can be applied on hierarchies with indentation */
	if (this.wrap) {
		config.align = config.align || {};

		config.align.wrap = this.wrap;
	}

	return config;
};

/**
 * Returns the assigned ID of the Type specific Style that is
 * registered in the Styles class.
 *
 * @param {Object} oData - Business Object that contains the property for hierarchy levels
 * @param {string|number} [sValue] - Explicit cell value that might be used for semantic formatting
 * @returns {number} - ID of the registered Style
 */
Text.prototype.getStyleId = function(oData, sValue) {
	var bRequiresPrefix, sIdentifier, oConfig;

	if (!oData) {
		return 0;
	}

	bRequiresPrefix = sValue && Text.formulaRegex.test(sValue);

	/* Handle simple styling without hierarchy information or prefix */
	if (!this.hierarchyLevel && !bRequiresPrefix) {
		return Object.getPrototypeOf(Text.prototype).getStyleId.call(this);
	}

	/* Create local identifier to differentiate between cached styleIds */
	sIdentifier = 'id_' + oData[this.hierarchyLevel] + (bRequiresPrefix ? '_prefix' : '');

	if (typeof this._mStyleIds[sIdentifier] === 'undefined') {
		oConfig = this._createConfiguration(oData);
		oConfig.quotePrefix = bRequiresPrefix;
		this._mStyleIds[sIdentifier] = this.styles.addStyle(oConfig);
	}

	return this._mStyleIds[sIdentifier];
};

/**
 * Returns the data value of the business object that is bound by
 * this type. If a template is configured, it will automatically fill
 * the template with the values from the business object and returns
 * the processed string.
 *
 * @param {string|Array} properties - Name of the property or array of properties that shall be accessed.
 * @param {Object} oData - Business object that contains the data.
 *
 * @returns {string|number|boolean} - The value of the given property after type specific processing.
 */
Text.prototype.getValue = function(properties, oData) {
	var aMatch, reRegEx, sResult, bSkip, sValue, aValues, aInputMatch;

	if (!this.template) {
		sValue = typeof properties === 'string' ? oData[properties] : oData[properties[0]];

		sValue = this.wrap ? Text.convertLineBreaks(sValue) : sValue;

		return sValue;
	}

	if (typeof this.inputFormat === 'string') {
		aInputMatch = new RegExp(this.inputFormat).exec(oData[properties]);

		/* Keeps the initial value in case the input does not match the regex pattern */
		aValues = Array.isArray(aInputMatch) ? aInputMatch.slice(1) : [oData[properties]];
	} else {
		aValues = properties.reduce(function(acc, property) {
			acc.push(oData[property]);

			return acc;
		}, []);
	}

	// Obtain if the further processing should be skipped due to missing values for the template
	bSkip = aValues.every(function(value) {
		return value === null
			|| typeof value === 'undefined'
			|| (typeof value === 'string' && value === '');
	});

	if (bSkip) {
		return '';
	}

	sResult = this.template;
	reRegEx = /{([0-9]+)}/g;

	aMatch = reRegEx.exec(this.template);
	while (aMatch !== null) {

		sValue = aValues[aMatch[1]];

		// Overwrite empty or undefined values by an empty string
		if (sValue === null || typeof sValue === 'undefined') {
			sValue = '';
		}

		// Replaces the matched placeholder by the indexed property value
		sResult = sResult.replace(aMatch[0], sValue);

		aMatch = reRegEx.exec(this.template);
	}

	return sResult;
};

module.exports = Text;


//////////////////
// WEBPACK FOOTER
// ./core/xlsx/src/binding/type/Text.js
// module id = 15
// module chunks = 0